import { Alert, Box, Button, Typography } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { apiUploadPropertyStep2 } from "../../../services/api/site-settings-api";
import { useParams } from "react-router-dom";
import { parseJSON } from "../../../utils/formatting";
import mapboxgl from "mapbox-gl";
import MapContext from "../../../services/context/map-context";
import { LoaderMUI } from "../../../assets/global/loader/loader-mui";
import { isMapExist } from "../../../utils/map-layer";

export const StepCenter = ({
  fileId,
  units,
  reselectCenterHandler,
  center,
  setCenter,
  saveGeoJSONHandler,
  loading,
  setLoading,
}) => {
  const { projectId } = useParams();
  const { map, mapLoaded } = useContext(MapContext);

  const [error, setError] = useState(null);

  const markerRef = useRef(null);

  const showMarker = (center) => {
    if (markerRef.current) markerRef.current.remove();
    markerRef.current = new mapboxgl.Marker({ color: "#343745" })
      .setLngLat([center.long, center.lat])
      .addTo(map);
  };

  const hideMarker = () => {
    if (markerRef.current) markerRef.current.remove();
    markerRef.current = null;
  };

  const reselectCenter = () => {
    hideMarker();
    reselectCenterHandler();
    map.getCanvas().style.cursor = "crosshair";
  };

  const getGeoJSON = (newCenter) => {
    if (!newCenter || !fileId || !units) return;

    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await apiUploadPropertyStep2(projectId, {
          fileId: fileId,
          units: units,
          center: newCenter,
        });
        /* console.log(data, parseJSON(data.geodata), "apiUploadFile step 2"); */

        if (data.geodata && parseJSON(data.geodata)) {
          const convertedGeoJSON = parseJSON(data.geodata);
          saveGeoJSONHandler(convertedGeoJSON, newCenter);
        }
      } catch (err) {
        /* console.log(err); */
        const message =
          err.response?.data?.message ||
          err.response?.data?.errormsg ||
          "An error occurred. Please try again or reload the page.";
        setError(message);
      } finally {
        setLoading(false);
      }
    })();
  };

  useEffect(() => {
    if (!map) return;
    map.getCanvas().style.cursor = "crosshair";

    const handleClick = (e) => {
      if (!center) {
        const newCenter = { lat: e.lngLat.lat, long: e.lngLat.lng };
        /* setCenter(center); */
        getGeoJSON(newCenter);
      }
    };

    if (center) {
      showMarker(center);
    }

    map.once("click", handleClick);

    return () => {
      if (map && mapLoaded && isMapExist(map)) {
        if (typeof map.off === "function") {
          map.off("click", handleClick);
        }

        hideMarker();

        map.getCanvas().style.cursor = "";
      }
    };
  }, [map, center]);

  return (
    <Box sx={{ p: 2 }}>
      {loading && <LoaderMUI withBackdrop={true} />}
      {center?.lat ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {/* <Typography textAlign={"center"} variant="body2">
                              Selected Center
                            </Typography> */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: "16px",
            }}
          >
            <Box>
              <Typography textAlign={"center"} color="#343745" fontWeight={500}>
                Latitude
              </Typography>
              <Typography textAlign={"center"} color="#777">
                {center.lat.toFixed(6)}
              </Typography>
            </Box>
            <Box
              sx={{
                width: "2px",
                height: "32px",
                backgroundColor: "#EBEBEC",
              }}
            />

            <Box>
              <Typography textAlign={"center"} color="#343745" fontWeight={500}>
                Longitude
              </Typography>
              <Typography textAlign={"center"} color="#777">
                {center.long.toFixed(6)}
              </Typography>
            </Box>
          </Box>
          <Button
            onClick={reselectCenter}
            sx={{
              background: "#fff",
              color: "#343745",
              textTransform: "none",
              padding: "6px 16px",
              boxShadow: 2,
              ":disabled": {
                color: "#C7C7D6",
                background: "#EEEEF6",
              },
              ":hover": {
                boxShadow: 4,
              },
            }}
          >
            Reselect
          </Button>
        </Box>
      ) : (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography
            variant="body1"
            fontSize={"14px"} /* sx={{ px: 2, py: 4 }} */
          >
            Click the map where the <strong>centre</strong> of the property
            should be placed. The drawing will appear there automatically.
          </Typography>
        </>
      )}
    </Box>
  );
};
